import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FilterField, TextInput, SelectInput, COLORS } from './InventoryUIKit';
import { Search, X, SlidersHorizontal } from 'lucide-react';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'Healthy', label: '🟢 Healthy' },
  { value: 'Low Stock', label: '🟡 Low Stock' },
  { value: 'Critical', label: '🔴 Critical' },
  { value: 'Exhausted', label: '⚫ Exhausted' },
];

const PurchaseTableToolbar = ({
  search,
  onSearchChange,
  filters,
  onFilterChange,
  onClear,
  supplierOptions = [],
}) => {
  const [localSearch, setLocalSearch] = useState(search);
  const debounceRef = useRef(null);

  useEffect(() => {
    setLocalSearch(search);
  }, [search]);

  const handleSearchChange = useCallback(
    (e) => {
      const val = e.target.value;
      setLocalSearch(val);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onSearchChange(val);
      }, 300);
    },
    [onSearchChange]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleFilterChange = useCallback(
    (key, value) => {
      onFilterChange({ ...filters, [key]: value });
    },
    [filters, onFilterChange]
  );

  const hasActiveFilters = search || filters.dateFrom || filters.dateTo || filters.supplier || filters.status;

  return (
    <div
      style={{
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.07)',
        padding: '16px 20px',
        marginBottom: '20px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
        <SlidersHorizontal size={16} color="#6b7280" />
        <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Filters & Search</span>
        {hasActiveFilters && (
          <button
            onClick={onClear}
            style={{
              marginLeft: 'auto',
              display: 'flex', alignItems: 'center', gap: '4px',
              padding: '4px 10px', borderRadius: '6px', border: '1px solid #e5e7eb',
              backgroundColor: '#ffffff', color: '#6b7280', fontSize: '12px', fontWeight: '500',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.borderColor = '#fecaca'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#ffffff'; e.currentTarget.style.color = '#6b7280'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
          >
            <X size={12} /> Clear All
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <FilterField label="Search" minWidth="220px">
          <div style={{ position: 'relative' }}>
            <Search
              size={14}
              color="#9ca3af"
              style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
            />
            <input
              type="text"
              placeholder="Invoice, item, code, supplier..."
              value={localSearch}
              onChange={handleSearchChange}
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '8px 10px 8px 32px',
                fontSize: '13px',
                color: '#1f2937',
                outline: 'none',
                backgroundColor: '#ffffff',
                fontFamily: "'Inter', sans-serif",
                width: '100%',
                boxSizing: 'border-box',
              }}
            />
          </div>
        </FilterField>

        <FilterField label="Date From" minWidth="140px">
          <TextInput
            type="date"
            value={filters.dateFrom || ''}
            onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
          />
        </FilterField>

        <FilterField label="Date To" minWidth="140px">
          <TextInput
            type="date"
            value={filters.dateTo || ''}
            onChange={(e) => handleFilterChange('dateTo', e.target.value)}
          />
        </FilterField>

        <FilterField label="Supplier" minWidth="160px">
          <SelectInput
            value={filters.supplier || ''}
            onChange={(e) => handleFilterChange('supplier', e.target.value)}
          >
            <option value="">All Suppliers</option>
            {supplierOptions.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </SelectInput>
        </FilterField>

        <FilterField label="Status" minWidth="150px">
          <SelectInput
            value={filters.status || ''}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </SelectInput>
        </FilterField>
      </div>
    </div>
  );
};

export default React.memo(PurchaseTableToolbar);
